/**
 * Third-party Solana wallets — injected providers only (no Auth2).
 * Aligns with GrudgeBuilder solana multi-wallet pattern.
 * Opt-in: never auto-connect on load.
 */

export type SolanaWalletId =
  | "phantom"
  | "solflare"
  | "backpack"
  | "glow"
  | "coinbase"
  | "exodus"
  | "nightly"
  | "trust"
  | "okx"
  | "bitget"
  | "magiceden"
  | "generic";

export interface DetectedWallet {
  id: SolanaWalletId;
  name: string;
  icon: string;
  available: boolean;
}

export interface ConnectedSolanaWallet {
  id: SolanaWalletId;
  name: string;
  publicKey: string;
  provider: SolanaProvider;
}

/** Injected / Phantom-SDK-compatible Solana provider surface */
export interface SolanaProvider {
  isPhantom?: boolean;
  isSolflare?: boolean;
  isBackpack?: boolean;
  isGlow?: boolean;
  isCoinbaseWallet?: boolean;
  isExodus?: boolean;
  isNightly?: boolean;
  isTrust?: boolean;
  isOkxWallet?: boolean;
  isBitKeep?: boolean;
  isMagicEden?: boolean;
  publicKey?: { toString(): string } | null;
  connect: (opts?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey: { toString(): string } } | void>;
  disconnect?: () => Promise<void>;
  signMessage?: (message: Uint8Array, display?: string) => Promise<Uint8Array | { signature: Uint8Array }>;
  /** Phantom / Solflare / standard injected: sign + broadcast */
  signAndSendTransaction?: (
    tx: unknown,
    opts?: {
      skipPreflight?: boolean;
      preflightCommitment?: string;
      maxRetries?: number;
      /** Phantom embedded dapp fee-payer hook */
      presignTransaction?: (
        tx: string,
        context: { networkId: string; walletId: string },
      ) => Promise<string>;
    },
  ) => Promise<{ signature: string } | string>;
  /** Sign only — app broadcasts (fallback path) */
  signTransaction?: (tx: unknown) => Promise<unknown>;
  request?: (args: { method: string; params?: unknown }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  off?: (event: string, handler: (...args: unknown[]) => void) => void;
}

declare global {
  interface Window {
    solana?: SolanaProvider;
    phantom?: { solana?: SolanaProvider };
    solflare?: SolanaProvider;
    backpack?: SolanaProvider;
    glowSolana?: SolanaProvider;
    coinbaseSolana?: SolanaProvider;
    exodus?: { solana?: SolanaProvider };
    nightly?: { solana?: SolanaProvider };
    trustwallet?: { solana?: SolanaProvider };
    okxwallet?: { solana?: SolanaProvider };
    bitkeep?: { solana?: SolanaProvider };
    magicEden?: { solana?: SolanaProvider };
  }
}

function getProvider(id: SolanaWalletId): SolanaProvider | null {
  if (typeof window === "undefined") return null;
  switch (id) {
    case "phantom":
      // Phantom multi-wallet / portal: prefer explicit phantom.solana (includes all accounts UX)
      return (
        window.phantom?.solana ??
        (window.solana?.isPhantom ? window.solana : null)
      );
    case "solflare":
      return (
        window.solflare ??
        (window.solana?.isSolflare ? window.solana : null)
      );
    case "backpack":
      return window.backpack ?? null;
    case "glow":
      return window.glowSolana ?? null;
    case "coinbase":
      return window.coinbaseSolana ?? null;
    case "exodus":
      return window.exodus?.solana ?? null;
    case "nightly":
      return window.nightly?.solana ?? null;
    case "trust":
      return window.trustwallet?.solana ?? null;
    case "okx":
      return window.okxwallet?.solana ?? null;
    case "bitget":
      return window.bitkeep?.solana ?? null;
    case "magiceden":
      return window.magicEden?.solana ?? null;
    case "generic":
      // Any other injected Solana provider (Wallet Standard shims often set window.solana)
      if (window.solana?.isPhantom || window.solana?.isSolflare) return null;
      return window.solana ?? null;
    default:
      return null;
  }
}

const CATALOG: Array<{ id: SolanaWalletId; name: string; icon: string }> = [
  { id: "phantom", name: "Phantom", icon: "👻" },
  { id: "solflare", name: "Solflare", icon: "🔥" },
  { id: "backpack", name: "Backpack", icon: "🎒" },
  { id: "glow", name: "Glow", icon: "✨" },
  { id: "coinbase", name: "Coinbase", icon: "🔵" },
  { id: "exodus", name: "Exodus", icon: "🪐" },
  { id: "nightly", name: "Nightly", icon: "🌙" },
  { id: "trust", name: "Trust", icon: "🛡️" },
  { id: "okx", name: "OKX", icon: "⬛" },
  { id: "bitget", name: "Bitget", icon: "🟩" },
  { id: "magiceden", name: "Magic Eden", icon: "🪄" },
  { id: "generic", name: "Solana Wallet", icon: "◎" },
];

/** Detect installed wallets (call only after user opens wallet UI). */
export function detectSolanaWallets(): DetectedWallet[] {
  return CATALOG.map((c) => ({
    ...c,
    available: Boolean(getProvider(c.id)),
  })).sort((a, b) => {
    // Available first; Phantom first among available (multi-account portal)
    if (a.available !== b.available) return a.available ? -1 : 1;
    if (a.id === "phantom") return -1;
    if (b.id === "phantom") return 1;
    return 0;
  });
}

/**
 * Prefer Phantom (all accounts / multi-wallet UX when installed), else first available.
 */
export function preferredWalletId(
  detected?: DetectedWallet[],
): SolanaWalletId | null {
  const list = detected ?? detectSolanaWallets();
  const phantom = list.find((w) => w.id === "phantom" && w.available);
  if (phantom) return "phantom";
  const any = list.find((w) => w.available);
  return any?.id ?? null;
}

export async function connectSolanaWallet(
  id: SolanaWalletId,
  opts?: { onlyIfTrusted?: boolean },
): Promise<ConnectedSolanaWallet> {
  const provider = getProvider(id);
  if (!provider) {
    throw new Error(`${id} wallet not installed — install from Connect tab`);
  }
  // onlyIfTrusted: true = silent rehydrate if already approved (page refresh)
  // false = show account picker for multi-account wallets
  const resp = await provider.connect({
    onlyIfTrusted: opts?.onlyIfTrusted === true,
  });
  const pk =
    (resp && "publicKey" in resp && resp.publicKey
      ? resp.publicKey.toString()
      : null) || provider.publicKey?.toString();
  if (!pk) throw new Error("Wallet did not return a public key");
  const meta = CATALOG.find((c) => c.id === id)!;
  return { id, name: meta.name, publicKey: pk, provider };
}

/**
 * Re-bind signing session after refresh when we already know the public key.
 * Tries silent trusted connect first; full connect only when silentOnly is false.
 */
export async function ensureSigningWallet(opts: {
  walletId?: SolanaWalletId | null;
  publicKey?: string | null;
  /** true = onlyIfTrusted only (panel open / balance refresh — no popup) */
  silentOnly?: boolean;
}): Promise<ConnectedSolanaWallet | null> {
  const id =
    opts.walletId && opts.walletId !== "generic"
      ? opts.walletId
      : preferredWalletId();
  if (!id) return null;

  // Already connected in-page with matching key
  const provider = getProvider(id);
  if (provider?.publicKey) {
    const pk = provider.publicKey.toString();
    if (!opts.publicKey || pk === opts.publicKey) {
      const meta = CATALOG.find((c) => c.id === id)!;
      return { id, name: meta.name, publicKey: pk, provider };
    }
  }

  try {
    const silent = await connectSolanaWallet(id, { onlyIfTrusted: true });
    if (!opts.publicKey || silent.publicKey === opts.publicKey) return silent;
  } catch {
    /* not trusted yet */
  }

  if (opts.silentOnly) return null;

  try {
    const full = await connectSolanaWallet(id, { onlyIfTrusted: false });
    if (!opts.publicKey || full.publicKey === opts.publicKey) return full;
    // Connected a different account — still return it (user chose)
    return full;
  } catch {
    return null;
  }
}

/** Connect preferred installed wallet (Phantom first). */
export async function connectPreferredWallet(): Promise<ConnectedSolanaWallet> {
  const id = preferredWalletId();
  if (!id) {
    throw new Error(
      "No Solana wallet extension found — install Phantom or Solflare",
    );
  }
  return connectSolanaWallet(id);
}

export async function disconnectSolanaWallet(
  wallet: ConnectedSolanaWallet | null,
): Promise<void> {
  try {
    await wallet?.provider.disconnect?.();
  } catch {
    /* ignore */
  }
}

/** Re-bind provider after page refresh when publicKey still known */
export async function reconnectSolanaWallet(
  id: SolanaWalletId,
): Promise<ConnectedSolanaWallet | null> {
  try {
    return await connectSolanaWallet(id);
  } catch {
    return null;
  }
}

/** Sign UTF-8 message → base58 signature (Railway wallet link expects base58). */
export async function signMessageBase58(
  wallet: ConnectedSolanaWallet,
  message: string,
): Promise<string> {
  if (!wallet.provider.signMessage) {
    throw new Error("Wallet does not support message signing");
  }
  const encoded = new TextEncoder().encode(message);
  const result = await wallet.provider.signMessage(encoded, "utf8");
  const sigBytes =
    result instanceof Uint8Array
      ? result
      : (result as { signature: Uint8Array }).signature;

  // Prefer bs58 if present; else hex fallback labeled for server
  try {
    const bs58 = await import("bs58");
    return bs58.default.encode(sigBytes);
  } catch {
    return Array.from(sigBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
}

export function installUrl(id: SolanaWalletId): string {
  switch (id) {
    case "phantom":
      return "https://phantom.app/";
    case "solflare":
      return "https://solflare.com/";
    case "backpack":
      return "https://www.backpack.app/";
    case "glow":
      return "https://glow.app/";
    case "coinbase":
      return "https://www.coinbase.com/wallet";
    case "exodus":
      return "https://www.exodus.com/";
    case "nightly":
      return "https://nightly.app/";
    case "trust":
      return "https://trustwallet.com/";
    case "okx":
      return "https://www.okx.com/web3";
    case "bitget":
      return "https://web3.bitget.com/";
    case "magiceden":
      return "https://magiceden.io/wallet";
    default:
      return "https://solana.com/ecosystem/explore?categories=wallet";
  }
}
